import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./lib/hooks/useAuth";
import { UserRole } from "./lib/constants";
import Loading from "./components/shared/Loading";
import { Toaster } from "react-hot-toast";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "./lib/react-query/query-client";
import { useEffect, lazy, Suspense } from "react";
import {
  initSentry,
  setUserContext,
  clearUserContext,
} from "./lib/sentry/sentry";

// ✅ Initialiser Sentry au démarrage
initSentry();

// ✅ Lazy Loading: Charger les pages uniquement quand nécessaire
// Login (chargé immédiatement car c'est la première page)
import LoginPage from "./app/(auth)/login/page";

// Public Pages
const DownloadPage = lazy(() => import("./app/download/page"));

// Admin Pages (lazy)
const AdminDashboard = lazy(() => import("./app/admin/dashboard/page"));
const StoresPage = lazy(() => import("./app/admin/stores/page"));
const UsersPage = lazy(() => import("./app/admin/users/page"));
const InventoryPage = lazy(() => import("./app/admin/inventory/page"));
const CollaborateursPage = lazy(
  () => import("./app/admin/collaborateurs/page"),
);
const FournisseursPage = lazy(() => import("./app/admin/fournisseurs/page"));
const SupportsPage = lazy(() => import("./app/admin/supports/page"));
const CategoriesPage = lazy(() => import("./app/admin/categories/page"));
const ProductsPage = lazy(() => import("./app/admin/products/page"));
const StoreProductsPage = lazy(
  () => import("./app/admin/products/[storeId]/page"),
);
const OrdersPage = lazy(() => import("./app/admin/orders/page"));
const CustomersPage = lazy(() => import("./app/admin/customers/page"));
const MessagingPage = lazy(() => import("./app/admin/messaging/page"));
const NotificationsPage = lazy(() => import("./app/admin/notifications/page"));
const SettingsPage = lazy(() => import("./app/admin/settings/page"));
const SuiviPage = lazy(() => import("./app/admin/suivi/page"));
const ImportGlovoPage = lazy(() => import("./app/admin/import-glovo/page"));
const ComptabilitePage = lazy(() => import("./app/admin/comptabilite/page"));
const LiensFournisseursPage = lazy(() => import("./app/admin/liens-fournisseurs/page"));
// Page publique : le lien de prix ouvert par le fournisseur, sans compte.
const TarifsFournisseurPage = lazy(() => import("./app/tarifs/page"));

// Collaborateur Pages (lazy)
const CollaborateurRemboursements = lazy(
  () => import("./app/collaborateur/remboursements/page"),
);
const CollaborateurDashboard = lazy(
  () => import("./app/collaborateur/dashboard/page"),
);

// Fournisseur Pages (lazy)
const FournisseurDashboard = lazy(
  () => import("./app/fournisseur/dashboard/page"),
);
const FournisseurHistory = lazy(() => import("./app/fournisseur/history/page"));
const FournisseurProducts = lazy(
  () => import("./app/fournisseur/products/page"),
);
const FournisseurHoraires = lazy(
  () => import("./app/fournisseur/horaires/page"),
);
const FournisseurRemboursements = lazy(
  () => import("./app/fournisseur/remboursements/page"),
);

// Support Pages (lazy)
const SupportDashboard = lazy(() => import("./app/support/dashboard/page"));
// La caisse du responsable commande. UNE page pour les trois roles : ce
// qu'elle montre depend du droit verifie par l'API, pas de l'ecran. Trois
// copies auraient diverge au premier correctif.
const CaissePage = lazy(() => import("./app/caisse/page"));
const LivreursPage = lazy(() => import("./app/livreurs/page"));
const RelevesFournisseursPage = lazy(() => import("./app/releves-fournisseurs/page"));
const SupportOrders = lazy(() => import("./app/support/orders/page"));
const SupportMessaging = lazy(() => import("./app/support/messaging/page"));
const SupportStores = lazy(() => import("./app/support/stores/page"));
const SupportProducts = lazy(() => import("./app/support/products/page"));
const SupportEmballages = lazy(
  () => import("./app/support/emballages/page"),
);
const SupportStoreProducts = lazy(
  () => import("./app/support/products/[storeId]/page"),
);
const SupportCategories = lazy(() => import("./app/support/categories/page"));

// Layouts (chargés immédiatement car nécessaires pour la structure)
import AdminLayout from "./components/layouts/AdminLayout";
import CollaborateurLayout from "./components/layouts/CollaborateurLayout";
import FournisseurLayout from "./components/layouts/FournisseurLayout";
import SupportLayout from "./components/layouts/SupportLayout";

// Protected Route Component
function ProtectedRoute({
  children,
  allowedRoles,
}: {
  children: React.ReactNode;
  allowedRoles: UserRole[];
}) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(user.role!)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { user } = useAuth();

  // ✅ Mettre à jour le contexte utilisateur dans Sentry
  useEffect(() => {
    if (user) {
      setUserContext({
        id: user.uid,
        email: user.email || undefined,
        role: user.role,
      });
    } else {
      clearUserContext();
    }
  }, [user]);

  // ✅ Plus de "Chargement..." - le user est disponible immédiatement depuis localStorage
  // Le loading ne bloque plus l'affichage, Firebase Auth vérifie en arrière-plan

  return (
    <QueryClientProvider client={queryClient}>
      {/* ✅ Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#fff",
            color: "#363636",
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          },
          success: {
            iconTheme: {
              primary: "#10b981",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      />

      <Suspense
        fallback={
          <div className="flex items-center justify-center min-h-screen">
            <Loading />
          </div>
        }
      >
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={!user ? <LoginPage /> : <Navigate to="/" replace />}
          />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/tarifs/:jeton" element={<TarifsFournisseurPage />} />

          {/* Protected routes - Admin */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="stores" element={<StoresPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="livreurs" element={<LivreursPage />} />
            <Route path="releves-fournisseurs" element={<RelevesFournisseursPage />} />
            <Route path="suivi" element={<SuiviPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="collaborateurs" element={<CollaborateursPage />} />
            <Route path="fournisseurs" element={<FournisseursPage />} />
            <Route path="supports" element={<SupportsPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="products/:storeId" element={<StoreProductsPage />} />
            <Route path="messaging" element={<MessagingPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="import-glovo" element={<ImportGlovoPage />} />
            <Route path="caisse" element={<CaissePage />} />
            <Route path="comptabilite" element={<ComptabilitePage />} />
            <Route path="liens-fournisseurs" element={<LiensFournisseursPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* Protected routes - Collaborateur */}
          <Route
            path="/collaborateur/*"
            element={
              <ProtectedRoute allowedRoles={[UserRole.COLLABORATEUR]}>
                <CollaborateurLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<CollaborateurDashboard />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route
              path="remboursements"
              element={<CollaborateurRemboursements />}
            />
            <Route path="caisse" element={<CaissePage />} />
          </Route>

          {/* Protected routes - Fournisseur */}
          <Route
            path="/fournisseur/*"
            element={
              <ProtectedRoute allowedRoles={[UserRole.FOURNISSEUR]}>
                <FournisseurLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<FournisseurDashboard />} />
            <Route path="history" element={<FournisseurHistory />} />
            <Route path="products" element={<FournisseurProducts />} />
            <Route path="horaires" element={<FournisseurHoraires />} />
            <Route
              path="remboursements"
              element={<FournisseurRemboursements />}
            />
            <Route path="orders" element={<OrdersPage />} />
          </Route>

          {/* Protected routes - Support */}
          <Route
            path="/support/*"
            element={
              <ProtectedRoute allowedRoles={[UserRole.SUPPORT]}>
                <SupportLayout />
              </ProtectedRoute>
            }
          >
            {/* Le support arrive sur le PILOTAGE : magasins ouverts, qui est
                connecte, demandes a trancher. C'est ce qu'il doit voir en
                premier — l'ancien tableau de chiffres reste a un clic. */}
            <Route index element={<Navigate to="pilotage" replace />} />
            <Route path="dashboard" element={<SupportDashboard />} />
            <Route path="orders" element={<SupportOrders />} />
            {/* La meme page que /admin/suivi, pas une copie : deux copies
                auraient diverge au premier correctif. Les droits sont deja
                ceux du support (ORDER_VIEW et ORDER_EDIT). */}
            <Route path="suivi" element={<SuiviPage />} />
            <Route path="messaging" element={<SupportMessaging />} />
            <Route path="stores" element={<SupportStores />} />
            <Route path="products" element={<SupportProducts />} />
            {/* Les emballages : le support tient la comptabilité, il doit
                pouvoir saisir la facture du fabricant lui-même. */}
            <Route path="emballages" element={<SupportEmballages />} />
            <Route
              path="products/:storeId"
              element={<SupportStoreProducts />}
            />
            <Route path="categories" element={<SupportCategories />} />
            {/* Meme page que cote administrateur : ce qu'elle autorise ne
                depend pas de l'ecran mais du droit verifie par l'API. */}
            <Route path="import-glovo" element={<ImportGlovoPage />} />
            {/* Meme page que cote administrateur : exports « All product »
                et imports des prix d'achat. Ce que le support peut y faire
                depend du droit verifie par l'API (PRODUCT_IMPORT_ALL), pas
                de l'ecran. */}
            <Route path="comptabilite" element={<ComptabilitePage />} />
            <Route path="caisse" element={<CaissePage />} />
            {/* Les livreurs (01/11/2026) : la meme page que la direction. */}
            <Route path="livreurs" element={<LivreursPage />} />
            {/* Releves fournisseurs : la direction seule (03/11/2026). */}
            {/* Le tableau de pilotage de la direction, ouvert au support :
                magasins ouverts, qui est connecte, demandes des
                fournisseurs. La MEME page, pas une copie — les chiffres
                reserves a la direction ne partent de toute facon pas du
                serveur vers un compte support. */}
            <Route path="pilotage" element={<AdminDashboard />} />
            {/* La page Fournisseurs de la direction, pour le seul support
                designe (18/10/2026). Le serveur verifie aussi le droit. */}
            <Route
              path="fournisseurs"
              element={
                user?.accesFournisseurs ? (
                  <FournisseursPage />
                ) : (
                  <Navigate to="/support/pilotage" replace />
                )
              }
            />
          </Route>

          {/* Root redirect */}
          <Route
            path="/"
            element={
              user ? (
                user.role === UserRole.ADMIN ? (
                  <Navigate to="/admin/dashboard" replace />
                ) : user.role === UserRole.COLLABORATEUR ? (
                  <Navigate to="/collaborateur/dashboard" replace />
                ) : user.role === UserRole.FOURNISSEUR ? (
                  <Navigate to="/fournisseur/dashboard" replace />
                ) : user.role === UserRole.SUPPORT ? (
                  <Navigate to="/support/pilotage" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </Suspense>

      {/* ✅ React Query DevTools (visible uniquement en dev pour Admin) */}
      {user?.role === UserRole.ADMIN && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
}

export default App;
